/* eslint-disable prettier/prettier */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const toSlug = (value) =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function CategoryMenuController({ categories, activeCategory = null }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    const updateButtons = () => {
      const maxLeft = container.scrollWidth - container.clientWidth;
      setCanScrollLeft(container.scrollLeft > 2);
      setCanScrollRight(container.scrollLeft < maxLeft - 2);
    };

    updateButtons();
    container.addEventListener("scroll", updateButtons);
    window.addEventListener("resize", updateButtons);

    return () => {
      container.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [categories]);

  const scrollByAmount = (amount) => {
    const container = scrollRef.current;
    if (!container) return;

    container.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        position: "relative",
        px: { xs: 3.8, md: 4.8 },
        py: 0.7,
        bgcolor: "#fff7f8",
        borderTop: "1px solid #fecdd3",
        borderBottom: "1px solid #fecdd3",
      }}
    >
      <IconButton
        aria-label="Scroll categories left"
        onClick={() => scrollByAmount(-280)}
        disabled={!canScrollLeft}
        size="small"
        sx={{
          position: "absolute",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 6px 14px rgba(15, 23, 42, 0.12)",
          "&:hover": { bgcolor: "#ffffff" },
          "&.Mui-disabled": { opacity: 0.4 },
          zIndex: 2,
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Stack
        ref={scrollRef}
        direction="row"
        spacing={3}
        sx={{
          overflowX: "auto",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {categories.map((menuItem, index) => (
          <Typography
            key={menuItem}
            component={Link}
            to={`/category/${toSlug(menuItem)}`}
            sx={{
              textDecoration: "none",
              color: activeCategory === menuItem ? "#b71c2a" : "#7f1d1d",
              fontWeight: activeCategory === menuItem ? 700 : 600,
              fontSize: { xs: "0.98rem", md: "0.8rem" },
              whiteSpace: "nowrap",
              transition: "color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
              borderRadius: 1,
              px: 1.2,
              py: 0.45,
              pr: index < categories.length - 1 ? 3 : 0,
              mr: index < categories.length - 1 ? 0.2 : 0,
              borderRight: index < categories.length - 1 ? "1px solid" : "none",
              borderColor: "#fda4af",
              "&:hover": {
                color: "#b71c2a",
                bgcolor: "#ffe4e6",
                boxShadow: "inset 0 0 0 1px #fecdd3",
              },
            }}
          >
            {menuItem}
          </Typography>
        ))}
      </Stack>

      <IconButton
        aria-label="Scroll categories right"
        onClick={() => scrollByAmount(280)}
        disabled={!canScrollRight}
        size="small"
        sx={{
          position: "absolute",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 6px 14px rgba(15, 23, 42, 0.12)",
          "&:hover": { bgcolor: "#ffffff" },
          "&.Mui-disabled": { opacity: 0.4 },
          zIndex: 2,
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

CategoryMenuController.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeCategory: PropTypes.string,
};

export default CategoryMenuController;
